/**
 * Grok AI Service - xAI API Integration
 * 
 * Uses server-side proxy endpoint for secure API access.
 * API Docs: https://docs.x.ai/docs/api-reference
 */

// Grok model configuration
const GROK_MODEL = 'grok-4-latest'; // Latest Grok 4 model

export interface GrokMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export interface GrokChatResponse {
  id: string;
  choices: Array<{
    index: number;
    message: {
      role: string;
      content: string;
    };
    finish_reason: string;
  }>;
  usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

export interface GrokStreamChunk {
  id: string;
  choices: Array<{
    delta: {
      content?: string;
    };
    finish_reason: string | null;
  }>;
}

class GrokAIService {
  /**
   * Send a chat completion request to Grok via server proxy
   */
  async chat(
    messages: GrokMessage[],
    options?: {
      model?: string;
      temperature?: number;
      maxTokens?: number;
      stream?: boolean;
    }
  ): Promise<GrokChatResponse> {
    // Use server-side proxy endpoint that has access to API key
    const response = await fetch('/api/grok/chat', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        messages: messages,
        model: options?.model || GROK_MODEL,
        temperature: options?.temperature ?? 0.7,
        max_tokens: options?.maxTokens ?? 1024,
        stream: false,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error || 
        `Grok API error: ${response.status} ${response.statusText}`
      );
    }

    return response.json();
  }

  /**
   * Stream a chat completion from Grok via server proxy
   */
  async *chatStream(
    messages: GrokMessage[],
    options?: {
      model?: string;
      temperature?: number;
      maxTokens?: number;
    }
  ): AsyncGenerator<string, void, unknown> {
    // Use server-side proxy endpoint that has access to API key
    const response = await fetch('/api/grok/chat', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        messages: messages,
        model: options?.model || GROK_MODEL,
        temperature: options?.temperature ?? 0.7,
        max_tokens: options?.maxTokens ?? 1024,
        stream: true,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error || 
        `Grok API error: ${response.status} ${response.statusText}`
      );
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response body');
    }

    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || trimmed === 'data: [DONE]') continue;
          
          if (trimmed.startsWith('data: ')) {
            try {
              const json = JSON.parse(trimmed.slice(6)) as GrokStreamChunk;
              const content = json.choices[0]?.delta?.content;
              if (content) {
                yield content;
              }
            } catch {
              // Skip malformed JSON
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  }

  /**
   * Quick helper for single-turn chat
   */
  async ask(
    question: string,
    systemPrompt?: string
  ): Promise<string> {
    const messages: GrokMessage[] = [];
    
    if (systemPrompt) {
      messages.push({ role: 'system', content: systemPrompt });
    }
    
    messages.push({ role: 'user', content: question });
    
    const response = await this.chat(messages);
    return response.choices[0]?.message?.content || '';
  }

  /**
   * Get oilfield-specific assistance
   */
  async askOilfield(question: string): Promise<string> {
    return this.ask(question, `You are a helpful AI assistant for ZiaWellNav, a navigation app for oilfield workers in the Permian Basin. You help with:
- Finding wells, SWD facilities, and rig locations
- Directions and navigation in remote oilfield areas
- Safety information and oilfield best practices
- Understanding facility data (API numbers, operators, coordinates)
- General oilfield industry questions

Be concise, practical, and focused on helping field workers. If you don't know something specific about a location, say so and suggest how they might find the information.`);
  }
}

// Singleton instance
export const grokAI = new GrokAIService();
export default grokAI;
